import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { usePreferences } from "@components/preferences";
import { Filter as F } from "@planzone/dto";
import { Filter } from "@common/filter";
import { UseCases } from "@planzone/core";
import { useRetrieveZoneProjectsReports } from "@planzone/view-models";
import { Identifiable, ZoneProjectReport } from "@planzone/dto";
import { useModal } from "@root/client/modal";
import { Report } from "./report";
import { Loader } from "./loader";
import { Toolbar } from "./toolbar";
import { Props, SortOptions } from "./types";
import styles from "./styles.scss";

function getWeek(date: Date) {
  const janFirst = new Date(date.getFullYear(), 0, 1);

  return Math.ceil(
    ((date.getTime() - janFirst.getTime()) / 86400000 + janFirst.getDay() + 1) /
      7,
  );
}

function isSameWeek(firstDate: Date, secondDate: Date) {
  return getWeek(firstDate) === getWeek(secondDate);
}

function sortReports(reports: ZoneProjectReport[]) {
  const now = new Date();
  const isSubmittedThisWeek = ({ report }: ZoneProjectReport) =>
    isSameWeek(new Date(report.submissionDate), now);
  const isSubmittedThisMonth = ({ report }: ZoneProjectReport) =>
    new Date(report.submissionDate).getMonth() === now.getMonth() &&
    new Date(report.submissionDate).getFullYear() === now.getFullYear();

  return reports.reduce<{
    week: ZoneProjectReport[];
    month: ZoneProjectReport[];
    others: ZoneProjectReport[];
  }>(
    (sortedReports, report) =>
      isSubmittedThisWeek(report)
        ? {
            ...sortedReports,
            week: [...sortedReports.week, report],
          }
        : isSubmittedThisMonth(report)
          ? {
              ...sortedReports,
              month: [...sortedReports.month, report],
            }
          : {
              ...sortedReports,
              others: [...sortedReports.others, report],
            },
    {
      week: [],
      month: [],
      others: [],
    },
  );
}

function orderReports(
  reports: ZoneProjectReport[],
  reverse: boolean,
): ZoneProjectReport[] {
  return reports.sort((current, next) => {
    const date1 = new Date(current.report.submissionDate);
    const date2 = new Date(next.report.submissionDate);
    return reverse
      ? date1.getTime() - date2.getTime()
      : date2.getTime() - date1.getTime();
  });
}

function ReportsPage({ zone }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const {
    reports,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveZoneProjectsReports,
  } = useRetrieveZoneProjectsReports(zone);
  const sortedReports = sortReports(reports);
  const { state, edit } = usePreferences();
  const { zoneProjectReportsFilter: filter } = state;
  const setFilter = (nextFilter: F) =>
    edit({ ...state, zoneProjectReportsFilter: nextFilter });
  const [sort, setSort] = useState(SortOptions.MostRecent);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveZoneProjectsReports(zone);
  }, [zone.uuid]);

  return (
    <div className={styles.reportsPage}>
      <Toolbar
        filter={filter}
        sort={sort}
        setFilter={setFilter}
        setSort={setSort}
      />
      {isRequestPending ? (
        <div className={styles.main}>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {t("zonePage.reportsPage.thisWeek")}
            </h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {t("zonePage.reportsPage.thisWeek")}
            </h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
          <div className={styles.section}>
            <h3 className={styles.title}>
              {t("zonePage.reportsPage.thisMonth")}
            </h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
          <div className={styles.section}>
            <h3 className={styles.title}>{t("zonePage.reportsPage.others")}</h3>
            <div className={styles.reportsList}>
              <Loader />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={classNames(styles.main, {
            [styles.reversedSections]: sort === SortOptions.Oldest,
          })}
        >
          <Filter
            data={orderReports(sortedReports.week, sort === SortOptions.Oldest)}
            filter={filter}
            extractCriteria={({ report, project }) => ({
              projectReport: report,
              project,
            })}
          >
            {(reports: Identifiable<ZoneProjectReport>[]) =>
              reports.length > 0 ? (
                <div className={styles.section}>
                  <h3 className={styles.title}>
                    {t("zonePage.reportsPage.thisWeek")}
                  </h3>
                  <div className={styles.reportsList}>
                    {reports.map((report) => (
                      <div
                        key={report.report.uuid}
                        className={styles.report}
                        onClick={() =>
                          pushView({
                            data: {
                              project: report.project,
                              reportId: report.report.uuid,
                              isMultiProject: true,
                            },
                            key: UseCases.retrieveProjectReport,
                          })
                        }
                      >
                        <Report {...report} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <React.Fragment />
              )
            }
          </Filter>
          <Filter
            data={orderReports(
              sortedReports.month,
              sort === SortOptions.Oldest,
            )}
            filter={filter}
            extractCriteria={({ report, project }) => ({
              projectReport: report,
              project,
            })}
          >
            {(reports: Identifiable<ZoneProjectReport>[]) =>
              reports.length > 0 ? (
                <div className={styles.section}>
                  <h3 className={styles.title}>
                    {t("zonePage.reportsPage.thisMonth")}
                  </h3>
                  <div className={styles.reportsList}>
                    {reports.map((report) => (
                      <div
                        key={report.report.uuid}
                        className={styles.report}
                        onClick={() =>
                          pushView({
                            data: {
                              project: report.project,
                              reportId: report.report.uuid,
                              isMultiProject: true,
                            },
                            key: UseCases.retrieveProjectReport,
                          })
                        }
                      >
                        <Report {...report} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <React.Fragment />
              )
            }
          </Filter>
          <Filter
            data={orderReports(
              sortedReports.others,
              sort === SortOptions.Oldest,
            )}
            filter={filter}
            extractCriteria={({ report, project }) => ({
              projectReport: report,
              project,
            })}
          >
            {(reports: Identifiable<ZoneProjectReport>[]) =>
              reports.length > 0 ? (
                <div className={styles.section}>
                  <h3 className={styles.title}>
                    {t("zonePage.reportsPage.others")}
                  </h3>
                  <div className={styles.reportsList}>
                    {reports.map((report) => (
                      <div
                        key={report.report.uuid}
                        className={styles.report}
                        onClick={() =>
                          pushView({
                            data: {
                              project: report.project,
                              reportId: report.report.uuid,
                              isMultiProject: true,
                            },
                            key: UseCases.retrieveProjectReport,
                          })
                        }
                      >
                        <Report {...report} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <React.Fragment />
              )
            }
          </Filter>
        </div>
      )}
    </div>
  );
}

export { ReportsPage };
